// src/services/apiClient.js
import { API_BASE_URL } from "../config";
// Assuming authService.js exports these:
import {
  getStoredAuth,
  logoutUser,
  refreshToken as attemptTokenRefresh, // Renamed to avoid conflict if refreshToken was a local var
} from "../services/authService";

// Default retry configuration (for non-401 errors)
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_INITIAL_DELAY_MS = 1000;
const DEFAULT_BACKOFF_FACTOR = 2;

const RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504];

// --- Token Refresh State ---
let isRefreshingToken = false;
let tokenRefreshPromise = null;
const requestQueue = []; // Stores { resolve, reject, retryRequestFn }

/**
 * Processes the queue of failed requests after a token refresh attempt.
 * @param {Error|null} error - The error from the token refresh attempt, or null on success.
 * @param {string|null} newToken - The new access token if refresh was successful.
 */
function processRequestQueue(error, newToken = null) {
  requestQueue.forEach((queuedRequest) => {
    if (error) {
      queuedRequest.reject(error); // Reject with the refresh error
    } else {
      // Signal to retry the request. The request function itself will pick up the new token.
      queuedRequest.resolveRetry();
    }
  });
  requestQueue.length = 0; // Clear the queue
}

/**
 * Handles the token refresh process.
 * Ensures only one refresh attempt is active.
 * On success, updates stored token and processes the request queue.
 * On failure, clears tokens (potentially) and processes the queue with an error.
 */
async function handleTokenRefresh() {
  if (!isRefreshingToken) {
    isRefreshingToken = true;
    tokenRefreshPromise = new Promise(async (resolve, reject) => {
      try {
        console.log("apiClient: Initiating token refresh.");
        // attemptTokenRefresh should:
        // 1. Make API call to refresh token using stored refresh token.
        // 2. If successful, store new access token (and new refresh token if rotated).
        // 3. Return new access token or relevant data.
        // 4. If failed (e.g. refresh token invalid), throw error & clear tokens.
        const { jwToken: newAccessToken } = await attemptTokenRefresh(); // From authService

        console.log("apiClient: Token refreshed successfully.");
        processRequestQueue(null, newAccessToken);
        resolve(newAccessToken);
      } catch (refreshError) {
        console.error("apiClient: Token refresh failed.", refreshError);
        processRequestQueue(refreshError, null);
        logoutUser();
        window.location.href = '/login';
        reject(refreshError);
      } finally {
        isRefreshingToken = false;
        tokenRefreshPromise = null;
      }
    });
  }
  return tokenRefreshPromise;
}

/**
 * Determines if an error is retryable (for non-401 errors).
 * @param {Error} err - The error object.
 * @returns {boolean}
 */
function isRetryableError(err) {
  if (
    err &&
    typeof err.status === "number" &&
    RETRYABLE_STATUS_CODES.includes(err.status)
  ) {
    return true;
  }
  if (
    err instanceof TypeError &&
    (err.message.toLowerCase().includes("failed to fetch") ||
      err.message.toLowerCase().includes("networkerror"))
  ) {
    return true;
  }
  return false;
}

/**
 * Delays execution.
 * @param {number} ms - Milliseconds to delay.
 * @returns {Promise<void>}
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Core fetch logic with response processing.
 */
async function _performFetchAndProcessResponse(
  endpoint,
  fetchOptions,
  requestTypeForLogging = "JSON",
  formDataForLogging = undefined
) {
  const url = `${API_BASE_URL.replace(/\/+$/g, "")}/${endpoint.replace(
    /^\/+/,
    ""
  )}`;
  try {
    const response = await fetch(url, fetchOptions);
    if (!response.ok) {
      let errorMessage = `API (${requestTypeForLogging}) request to ${endpoint} failed with status ${response.status}`;
      let errorDetails = null;
      let errorBodyText = "";
      try {
        errorBodyText = await response.text();
        if (errorBodyText) {
          try {
            errorDetails = JSON.parse(errorBodyText);
            errorMessage += `: ${
              errorDetails.title ||
              errorDetails.message ||
              JSON.stringify(errorDetails)
            }`;
          } catch (parseError) {
            errorMessage += `: ${errorBodyText.substring(0, 200)}${
              errorBodyText.length > 200 ? "..." : ""
            }`;
          }
        } else {
          errorMessage += `: ${response.statusText || "No error message body"}`;
        }
      } catch (textReadError) {
        errorMessage += `: ${
          response.statusText || "Failed to read error response body"
        }`;
      }
      const logPayload = {
        endpoint,
        method: fetchOptions.method,
        status: response.status,
        errorDetails,
        rawErrorBodyPreview: errorBodyText.substring(0, 500),
      };
      if (requestTypeForLogging === "FormData" && formDataForLogging)
        logPayload.formDataKeys = Array.from(formDataForLogging.keys());
      else if (fetchOptions.body && typeof fetchOptions.body === "string")
        logPayload.requestBodyPreview =
          fetchOptions.body.substring(0, 200) +
          (fetchOptions.body.length > 200 ? "..." : "");
      console.error(errorMessage, logPayload);
      const apiError = new Error(errorMessage);
      apiError.details = errorDetails;
      apiError.status = response.status;
      apiError.response = response;
      apiError.rawBody = errorBodyText;
      throw apiError;
    }
    const contentType = response.headers.get("content-type") || "";
    if (response.status === 204) return null;
    if (contentType.includes("application/json")) return await response.json();
    if (
      contentType.includes("text/markdown") ||
      contentType.includes("text/plain")
    )
      return await response.text();
    const textContent = await response.text();
    if (textContent) {
      try {
        return JSON.parse(textContent);
      } catch {
        console.warn(
          `Received OK response from ${endpoint} (${requestTypeForLogging}) with Content-Type '${contentType}' but couldn't parse as JSON. Returning as text.`
        );
        return textContent;
      }
    }
    return null;
  } catch (err) {
    if (err.message.startsWith(`API (${requestTypeForLogging}) request to`))
      throw err;
    console.error(
      `Network or other client-side error during ${requestTypeForLogging} request to ${endpoint}:`,
      err.name,
      err.message,
      err.stack?.substring(0, 300)
    );
    throw err;
  }
}

/**
 * A generic request execution function that incorporates 401 handling, queuing, and retries for other errors.
 * @param {Function} makeRequestFn - A function that, when called, prepares and executes the fetch using _performFetchAndProcessResponse.
 * @param {string} endpoint - API endpoint, for logging.
 * @param {object} retryConfig - Configuration for retrying non-401 errors.
 * @param {boolean} isRetryAfterRefresh - Internal flag to indicate if this attempt is immediately after a token refresh.
 * @returns {Promise<any>}
 */
async function _executeRequestWithHandling(
  makeRequestFn, // Example: () => _performFetchAndProcessResponse(endpoint, fetchOptions, "JSON")
  endpoint,
  retryConfig,
  isRetryAfterRefresh = false
) {
  try {
    return await makeRequestFn();
  } catch (error) {
    if (error && error.status === 401 && !isRetryAfterRefresh) {
      console.warn(
        `apiClient: Request to ${endpoint} received 401. Attempting token refresh.`
      );
      if (isRefreshingToken) {
        console.log(
          `apiClient: Token refresh already in progress. Queuing request to ${endpoint}.`
        );
        return new Promise((resolve, reject) => {
          requestQueue.push({
            resolveRetry: () => {
              console.log(
                `apiClient: Retrying queued request to ${endpoint} after token refresh.`
              );
              // Retry the entire _executeRequestWithHandling, now as a retryAfterRefresh
              _executeRequestWithHandling(
                makeRequestFn,
                endpoint,
                retryConfig,
                true
              )
                .then(resolve)
                .catch(reject);
            },
            reject, // Reject with the error from tokenRefreshPromise if it fails
          });
        });
      } else {
        try {
          await handleTokenRefresh(); // Initiates refresh if not already active
          console.log(
            `apiClient: Token refresh successful. Retrying original request to ${endpoint}.`
          );
          // Retry this request, now marked as an attempt after refresh
          return await _executeRequestWithHandling(
            makeRequestFn,
            endpoint,
            retryConfig,
            true
          );
        } catch (refreshError) {
          console.error(
            `apiClient: Token refresh failed for ${endpoint}. Propagating original 401 error or refresh error.`,
            refreshError
          );
          throw error; // Throw original 401, or refreshError if more appropriate
        }
      }
    } else if (error && error.status === 401 && isRetryAfterRefresh) {
      // If it's still 401 even after a refresh attempt, then the new token is also invalid or permissions are missing.
      console.error(
        `apiClient: Request to ${endpoint} failed with 401 even after token refresh. No further retries for 401.`
      );
      throw error; // Propagate the 401
    }

    // Handle non-401 retryable errors (uses the original retry logic)

    // Re-integrate your original retry loop for non-401 errors here.
    // For this example, I'm throwing to show it's not a 401 handled by refresh.
    // The outer fetchFromBackend/fetchWithFormData will manage its own retry loop based on this.
    throw error;
  }
}

export async function fetchFromBackend(
  endpoint,
  options = {},
  method = "POST",
  retryConfigParam = {} // Renamed to avoid conflict with internal retryConfig
) {
  // This function sets up the actual fetch call and headers
  const prepareAndExecuteFetch = async () => {
    const { body, headers: customHeaders = {}, ...restOptions } = options;
    const { token } = getStoredAuth(); // Gets the latest token (potentially refreshed)

    const defaultHeaders = {
      Accept: "application/json, text/markdown, text/plain",
    };
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    } else {
      console.warn(`apiClient: No token available for request to ${endpoint}.`);
    }

    const fetchOptions = {
      method,
      headers: { ...defaultHeaders, ...customHeaders },
      ...restOptions,
    };

    if (body && !["GET", "HEAD"].includes(method.toUpperCase())) {
      fetchOptions.headers["Content-Type"] = "application/json";
      fetchOptions.body = JSON.stringify(body);
    }
    return _performFetchAndProcessResponse(endpoint, fetchOptions, "JSON");
  };

  // Retry loop for non-401 errors, integrating the 401 handling
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
    backoffFactor = DEFAULT_BACKOFF_FACTOR,
  } = retryConfigParam;

  let executions = 0;
  let currentDelayMs = initialDelayMs;

  // A function to make a single attempt, incorporating 401 handling
  const attemptOnce = (isRetryAfterRefresh = false) => {
    return _executeRequestWithHandling(
      prepareAndExecuteFetch,
      endpoint,
      retryConfigParam,
      isRetryAfterRefresh
    );
  };
  var currentIsRetryAfterRefresh = true;
  while (true) {
    try {
      // The first attempt is not a retryAfterRefresh. Subsequent calls from queue/refresh handler will set it.
      // The _executeRequestWithHandling will handle the 401. If it re-throws an error
      // that is not a 401 (or a 401 after refresh), then the below retry logic kicks in.
      return await attemptOnce(executions > 0 && currentIsRetryAfterRefresh); // Pass retry state if needed
    } catch (err) {
      // If err is 401, it means either:
      // 1. Refresh process failed and _executeRequestWithHandling threw it.
      // 2. It was a 401 after a refresh attempt.
      // In these cases, we should not use the general retry logic for 401.
      if (err.status === 401) {
        throw err; // Propagate 401, do not retry here
      }

      executions++;
      if (executions > maxRetries || !isRetryableError(err)) {
        if (!err.message.startsWith("API (JSON) request to")) {
          console.error(
            `fetchFromBackend: Final attempt for ${endpoint} failed after ${
              executions - 1
            } retries:`,
            err.name,
            err.message
          );
        }
        throw err;
      }

      let actualDelayMs = currentDelayMs;
      if (err.status === 429 && err.response) {
        const retryAfterHeader = err.response.headers.get("Retry-After");
        if (retryAfterHeader) {
          const retryAfterSeconds = parseInt(retryAfterHeader, 10);
          if (!isNaN(retryAfterSeconds)) {
            const retryAfterMs = retryAfterSeconds * 1000;
            actualDelayMs = Math.max(currentDelayMs, retryAfterMs);
            console.warn(
              `fetchFromBackend: Received 429 for ${endpoint}. Using delay of ${
                actualDelayMs / 1000
              }s.`
            );
          }
        }
      }

      console.warn(
        `fetchFromBackend: Non-401 Error for ${endpoint}. Retrying in ${
          actualDelayMs / 1000
        }s (attempt ${executions}/${maxRetries}). Error: ${err.message.substring(
          0,
          150
        )}`
      );
      await delay(actualDelayMs);
      currentDelayMs *= backoffFactor;
      // A variable to track if the next attempt in the loop is a retry after refresh;
      // This would typically be false unless the error that brought us here was somehow related
      // to a refresh that completed but the subsequent request failed with a retryable non-401.
      // For simplicity, assuming normal retry loop iterations are not "isRetryAfterRefresh".
      currentIsRetryAfterRefresh = false;
    }
  }
}

export async function fetchWithFormData(
  endpoint,
  formData,
  options = {},
  retryConfigParam = {}
) {
  const prepareAndExecuteFetch = async () => {
    const { headers: customHeaders = {}, ...restOptions } = options;
    const { token } = getStoredAuth();

    const defaultHeaders = {
      Accept: "application/json, text/plain",
    };
    if (token) {
      defaultHeaders.Authorization = `Bearer ${token}`;
    } else {
      console.warn(
        `apiClient: No token available for FormData request to ${endpoint}.`
      );
    }
    // For FormData, Content-Type is set by the browser. Do not set it manually.

    const fetchOptions = {
      method: "POST",
      body: formData, // FormData reusability for retries can be an issue with streams.
      headers: { ...defaultHeaders, ...customHeaders },
      // credentials: "include", // Keep if cross-origin cookies are needed
      ...restOptions,
    };
    return _performFetchAndProcessResponse(
      endpoint,
      fetchOptions,
      "FormData",
      formData
    );
  };

  // Retry loop for non-401 errors
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
    backoffFactor = DEFAULT_BACKOFF_FACTOR,
  } = retryConfigParam;

  let executions = 0;
  let currentDelayMs = initialDelayMs;

  const attemptOnce = (isRetryAfterRefresh = false) => {
    return _executeRequestWithHandling(
      prepareAndExecuteFetch,
      endpoint,
      retryConfigParam,
      isRetryAfterRefresh
    );
  };
  var currentIsRetryAfterRefresh = true;
  while (true) {
    try {
      return await attemptOnce(executions > 0 && currentIsRetryAfterRefresh);
    } catch (err) {
      if (err.status === 401) {
        throw err;
      }

      executions++;
      if (executions > maxRetries || !isRetryableError(err)) {
        if (!err.message.startsWith("API (FormData) request to")) {
          console.error(
            `fetchWithFormData: Final attempt for ${endpoint} failed after ${
              executions - 1
            } retries:`,
            err.name,
            err.message
          );
        }
        throw err;
      }

      let actualDelayMs = currentDelayMs;
      if (err.status === 429 && err.response) {
        // ... (Retry-After logic as in fetchFromBackend)
        const retryAfterHeader = err.response.headers.get("Retry-After");
        if (retryAfterHeader) {
          const retryAfterSeconds = parseInt(retryAfterHeader, 10);
          if (!isNaN(retryAfterSeconds)) {
            actualDelayMs = Math.max(currentDelayMs, retryAfterSeconds * 1000);
            console.warn(
              `fetchWithFormData: Received 429 for ${endpoint}. Using delay of ${
                actualDelayMs / 1000
              }s.`
            );
          }
        }
      }

      console.warn(
        `fetchWithFormData: Non-401 Error for ${endpoint}. Retrying in ${
          actualDelayMs / 1000
        }s (attempt ${executions}/${maxRetries}). Error: ${err.message.substring(
          0,
          150
        )}`
      );
      await delay(actualDelayMs);
      currentDelayMs *= backoffFactor;
      currentIsRetryAfterRefresh = false; // See comment in fetchFromBackend
    }
  }
}
